import { useAuth } from "src/contexts/auth-provider";
import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { ModifierCategoryFormProps } from "src/interfaces/modifier-category-props.interface";
import { useAddModifierCategory } from "../hooks/useAddModifierCategory";
import { useUpdateModifierCategory } from "../hooks/useUpdateModifierCategory";
import Button from "src/components/common/Button";
import MenuItemLocaleFields from "src/components/common/MenuItemLocaleFields";
import { useStorePosLocales } from "src/hooks/useStorePosLocales";
import {
  buildLocaleTranslationMap,
  buildTranslationsPayload,
  LocaleTranslationMap,
  syncLegacyNameFieldsFromLocaleTranslations,
} from "src/utils/localeTranslations";

const ModifierCategoryForm = ({
  modifierCategoryToEdit,
  handleCloseModifierCategoryModal,
}: ModifierCategoryFormProps) => {
  const { hasRestaurant } = useAuth();
  const { addModifierCategory, isAddingModifierCategory } = useAddModifierCategory();
  const { updateModifierCategory, isUpdatingModifierCategory } = useUpdateModifierCategory();
  const { enabledLocaleOptions, enabledLocales, defaultLocale } = useStorePosLocales();

  const initialLocaleTranslations = useMemo(
    () =>
      buildLocaleTranslationMap(modifierCategoryToEdit?.translations, enabledLocales, {
        englishName: modifierCategoryToEdit?.name,
        nonEnglishName: modifierCategoryToEdit?.nonEnglishName,
      }),
    [modifierCategoryToEdit, enabledLocales]
  );

  const [localeTranslations, setLocaleTranslations] = useState<LocaleTranslationMap>(initialLocaleTranslations);

  useEffect(() => {
    setLocaleTranslations(initialLocaleTranslations);
  }, [initialLocaleTranslations]);

  const buttonText = modifierCategoryToEdit ? "Update" : "Save";
  const isLoadingModifierCategory = isAddingModifierCategory || isUpdatingModifierCategory;

  const formik = useFormik({
    initialValues: {
      isActive: modifierCategoryToEdit ? modifierCategoryToEdit.active !== false : true,
    },
    onSubmit: async (_values, { resetForm }) => {
      const legacy = syncLegacyNameFieldsFromLocaleTranslations(localeTranslations);
      const translations = buildTranslationsPayload(localeTranslations, enabledLocales);
      const payload = {
        name: legacy.name,
        nonEnglishName: legacy.nonEnglishName || undefined,
        translations,
      };

      if (modifierCategoryToEdit?.correlationId) {
        await updateModifierCategory({
          modifierCategoryCorrelationId: modifierCategoryToEdit.correlationId,
          ...payload,
          isActive: formik.values.isActive,
        });
      } else {
        await addModifierCategory({
          ...payload,
          storeCorrelationId: hasRestaurant?.correlationId,
        });
      }
      handleCloseModifierCategoryModal();
      resetForm();
    },
    enableReinitialize: true,
  });

  const defaultLocaleName = localeTranslations[defaultLocale]?.name?.trim() ?? "";
  const nameError = !defaultLocaleName ? "Name is Required" : undefined;

  return (
    <div className="w-full max-w-[464px]">
      <MenuItemLocaleFields
        enabledLocaleOptions={enabledLocaleOptions}
        defaultLocale={defaultLocale}
        localeTranslations={localeTranslations}
        onChange={setLocaleTranslations}
        nameError={nameError}
        entityLabel="Category"
        showDescription={false}
      />

      {modifierCategoryToEdit ? (
        <div className="mt-4">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formik.values.isActive !== false}
              onChange={(e) => formik.setFieldValue("isActive", e.target.checked)}
              className="receipt-lang-check w-6 h-6 min-w-[24px] min-h-[24px] shrink-0 rounded border border-[#9B9B9B] cursor-pointer appearance-none"
            />
            <span className="text-sm">Modifier category is active</span>
          </label>
        </div>
      ) : null}

      <div className="flex justify-end mt-[30px]">
        <Button
          isLoading={isLoadingModifierCategory}
          disabled={!defaultLocaleName}
          onClick={() => formik.handleSubmit()}
        >
          {buttonText}
        </Button>
      </div>
    </div>
  );
};

export default ModifierCategoryForm;
